import { FormFieldConfig } from '@shared/components/search-form/search-form.component';
import { TableColumn } from '@shared/components/data-table/data-table.component';

// ============================================================================
// INTERFACES
// ============================================================================

export interface CompanyItem {
  id: string;
  code: string;
  name: string;
  status: string;
  creationDate: string;
  lastModifiedDate?: string;
  shopPermitName?: string;
  legalPerson?: string;
  businessLicense?: string;
}

export interface CompanyListResponse {
  data: CompanyItem[];
  totalRecords: number;
  loading: boolean;
}

// ============================================================================
// STATUS OPTIONS
// ============================================================================

export const COMPANY_STATUS_OPTIONS = [
  { label: 'organizational.company.status.active', value: 'ACTIVE' },
  { label: 'organizational.company.status.inactive', value: 'INACTIVE' },
];

// Status options used by list filter
export const COMPANY_STATUS_FILTER_OPTIONS = [
  { label: 'organizational.company.status.active', value: 'ACTIVE' },
  { label: 'organizational.company.status.inactive', value: 'INACTIVE' },
];

// ============================================================================
// SEARCH FORM CONFIGURATION
// ============================================================================

export const COMPANY_SEARCH_CONFIG: FormFieldConfig[] = [
  {
    key: 'code',
    label: 'organizational.company.search.fields.code',
    type: 'text',
    placeholder: 'organizational.company.search.placeholders.enter_code',
  },
  {
    key: 'name',
    label: 'organizational.company.search.fields.name',
    type: 'text',
    placeholder: 'organizational.company.search.placeholders.enter_name',
  },
  {
    key: 'status',
    label: 'organizational.company.search.fields.status',
    type: 'select',
    options: COMPANY_STATUS_FILTER_OPTIONS,
    placeholder: 'organizational.company.search.placeholders.select_status',
    showClear: true,
  },
];

// ============================================================================
// TABLE COLUMN CONFIGURATION
// ============================================================================

/**
 * Default column property names (shown on first load)
 * Update these constants to change column property names
 */
export const COMPANY_DEFAULT_COLUMNS = {
  CODE: 'code',
  NAME: 'name',
  SHOP_PERMIT_NAME: 'shopPermitName',
  STATUS: 'status',
} as const;

/**
 * Flexible column property names (can be shown/hidden via column selector)
 * Update these constants to change flexible column property names
 */
export const COMPANY_FLEXIBLE_COLUMNS = {
  CREATION_DATE: 'creationDate',
} as const;

/**
 * Action column property name
 */
export const COMPANY_ACTION_COLUMN = {
  ACTIONS: 'actions',
} as const;

/**
 * Default columns order (shown on first load)
 * Update this array to change the default column order
 */
export const COMPANY_DEFAULT_COLUMNS_ORDER: string[] = [
  COMPANY_DEFAULT_COLUMNS.CODE,
  COMPANY_DEFAULT_COLUMNS.NAME,
  COMPANY_DEFAULT_COLUMNS.SHOP_PERMIT_NAME,
  COMPANY_DEFAULT_COLUMNS.STATUS,
];

/**
 * Flexible columns array (can be shown/hidden via column selector)
 * Update this array to change which columns are flexible
 */
export const COMPANY_FLEXIBLE_COLUMNS_ORDER: string[] = [
  COMPANY_FLEXIBLE_COLUMNS.CREATION_DATE,
];

export const COMPANY_COLUMNS: TableColumn[] = [
  {
    label: 'organizational.company.table.columns.code',
    prop: COMPANY_DEFAULT_COLUMNS.CODE,
    width: 120,
  },
  {
    label: 'organizational.company.table.columns.name',
    prop: COMPANY_DEFAULT_COLUMNS.NAME,
    width: 200,
  },
  {
    label: 'organizational.company.table.columns.registered_business_name',
    prop: COMPANY_DEFAULT_COLUMNS.SHOP_PERMIT_NAME,
    width: 180,
  },
  {
    label: 'organizational.company.table.columns.status',
    prop: COMPANY_DEFAULT_COLUMNS.STATUS,
    width: 120,
    type: 'status',
  },
  {
    label: 'organizational.company.table.columns.creation_date',
    prop: COMPANY_FLEXIBLE_COLUMNS.CREATION_DATE,
    width: 135,
    type: 'custom',
    template: 'dateTemplate',
  },
  {
    label: 'organizational.company.table.columns.actions',
    prop: COMPANY_ACTION_COLUMN.ACTIONS,
    type: 'actions',
    width: 150,
  },
];

// ============================================================================
// STATUS HELPERS
// ============================================================================

export function getStatusSeverity(
  status: string
): 'success' | 'warning' | 'danger' | 'secondary' {
  switch (status?.toUpperCase()) {
    case 'ACTIVE':
      return 'success';
    case 'INACTIVE':
      return 'secondary';
    default:
      return 'secondary';
  }
}

export function getStatusDisplayText(
  status: string,
  translateService: any
): string {
  const statusKey = `organizational.company.status.${status?.toLowerCase()}`;
  return translateService.instant(statusKey) || status;
}

export function getStatusStyle(status: string): any {
  const baseStyle = {
    borderRadius: '12px',
    padding: '4px 12px',
    fontSize: '12px',
    fontWeight: '500',
  };

  return baseStyle;
}
